import React, { useCallback, useEffect, useRef } from "react";

// A LinkedIn-composer-style rich text editor: a plain contentEditable div
// driven by document.execCommand, storing/emitting raw HTML — same pattern
// as webridge's LinkedInEditor.tsx (see PATTERNS.md), ported to Tailwind
// instead of that project's SCSS. execCommand is deprecated but still
// universally supported; this trades long-term robustness for matching the
// reference implementation exactly, same tradeoff webridge made.
interface Props {
  content: string;
  onChange: (html: string) => void;
}

const TOOLBAR_BUTTON =
  "px-2.5 py-1.5 rounded-md text-sm text-secondary-dark hover:bg-white transition-colors";

export default function RichTextEditor({ content, onChange }: Props) {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== content) {
      editorRef.current.innerHTML = content || "<p><br></p>";
    }
  }, [content]);

  const exec = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const handleInput = useCallback(() => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }, [onChange]);

  function insertLink() {
    const url = window.prompt("链接地址：");
    if (url) exec("createLink", url);
  }

  return (
    <div className="border border-fourth-light rounded-lg overflow-hidden bg-white">
      <div className="flex flex-wrap gap-1 px-3 py-2 border-b border-fourth-light bg-c-gray/60">
        <button type="button" className={`${TOOLBAR_BUTTON} font-bold`} onMouseDown={(e) => { e.preventDefault(); exec("bold"); }} title="加粗">
          B
        </button>
        <button type="button" className={`${TOOLBAR_BUTTON} italic`} onMouseDown={(e) => { e.preventDefault(); exec("italic"); }} title="斜体">
          I
        </button>
        <button type="button" className={`${TOOLBAR_BUTTON} underline`} onMouseDown={(e) => { e.preventDefault(); exec("underline"); }} title="下划线">
          U
        </button>
        <span className="w-px bg-fourth-light mx-1" />
        <button type="button" className={TOOLBAR_BUTTON} onMouseDown={(e) => { e.preventDefault(); exec("formatBlock", "h2"); }}>
          H1
        </button>
        <button type="button" className={TOOLBAR_BUTTON} onMouseDown={(e) => { e.preventDefault(); exec("formatBlock", "h3"); }}>
          H2
        </button>
        <button type="button" className={TOOLBAR_BUTTON} onMouseDown={(e) => { e.preventDefault(); exec("formatBlock", "p"); }}>
          正文
        </button>
        <span className="w-px bg-fourth-light mx-1" />
        <button type="button" className={TOOLBAR_BUTTON} onMouseDown={(e) => { e.preventDefault(); exec("insertUnorderedList"); }} title="无序列表">
          • 列表
        </button>
        <button type="button" className={TOOLBAR_BUTTON} onMouseDown={(e) => { e.preventDefault(); exec("insertOrderedList"); }} title="有序列表">
          1. 列表
        </button>
        <button type="button" className={TOOLBAR_BUTTON} onMouseDown={(e) => { e.preventDefault(); exec("formatBlock", "blockquote"); }} title="引用">
          引用
        </button>
        <button type="button" className={TOOLBAR_BUTTON} onMouseDown={(e) => { e.preventDefault(); insertLink(); }} title="插入链接">
          链接
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        data-placeholder="开始撰写正文…"
        className="min-h-[360px] px-5 py-4 text-sm leading-relaxed outline-none [&_p]:mb-3 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_blockquote]:border-l-2 [&_blockquote]:border-fourth-light [&_blockquote]:pl-4 [&_blockquote]:text-secondary-light [&_a]:text-gold [&_a]:underline empty:before:content-[attr(data-placeholder)] empty:before:text-secondary-light/60"
      />
    </div>
  );
}
